/**
 * KAN-826 этап 3: фильтры доски CRM по городу и организации (текст и id из
 * ссылки с карточки организации). Состояние держит страница (в адресе).
 * KAN-906: compact - без подписей над полями, высота h-8 (таблица CRM).
 */
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { COMPACT_TRIGGER_CLASS, FILTER_TRIGGER_CLASS, FilterField } from '@/components/clients/FilterField';

/** Пункт "Все города": фильтра нет. */
const ALL_CITIES = '__all__';

export interface CrmLocationFilterValue {
  city: string | null;
  company: string | null;
  companyId: string | null;
}

interface CrmLocationFiltersProps {
  value: CrmLocationFilterValue;
  cities: readonly string[];
  /** Название организации для чипа ?company_id= (null: "выбранная"). */
  companyIdName: string | null;
  onChange: (next: Partial<CrmLocationFilterValue>) => void;
  onReset: () => void;
  /** KAN-906: компактная строка: высота h-8, подписи внутри полей. */
  compact?: boolean;
}

export function CrmLocationFilters({
  value,
  cities,
  companyIdName,
  onChange,
  onReset,
  compact = false,
}: CrmLocationFiltersProps): JSX.Element {
  const selectedCity = value.city ? cities.find((c) => c.toLocaleLowerCase('ru') === value.city?.trim().toLocaleLowerCase('ru')) : undefined;
  const anySet = !!(value.city || value.company || value.companyId);
  const height = compact ? 'h-8' : 'h-9';

  return (
    <>
      <FilterField label="Город" hideLabel={compact}>
        <Select
          value={selectedCity ?? ALL_CITIES}
          onValueChange={(v) => onChange({ city: v === ALL_CITIES ? null : v })}
        >
          <SelectTrigger data-testid="city-filter" aria-label="Город" className={compact ? COMPACT_TRIGGER_CLASS : FILTER_TRIGGER_CLASS}>
            <SelectValue placeholder="Город" className="whitespace-nowrap" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CITIES}>Все города</SelectItem>
            {cities.map((city) => (
              <SelectItem key={city} value={city}>
                {city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label="Организация" hideLabel={compact}>
        <Input
          data-testid="company-filter"
          aria-label="Организация"
          placeholder={compact ? 'Организация' : 'Название'}
          className={compact ? 'h-8 w-full text-sm sm:w-44' : 'h-9 w-full sm:w-56'}
          value={value.company ?? ''}
          onChange={(e) => onChange({ company: e.target.value || null })}
        />
      </FilterField>

      {value.companyId && (
        <span
          data-testid="company-id-chip"
          className={`inline-flex ${height} max-w-full items-center gap-1 rounded-full border bg-gray-50 pl-3 pr-1 text-sm`}
        >
          <span className="truncate">Организация: {companyIdName ?? 'выбранная'}</span>
          <button
            type="button"
            aria-label="Убрать фильтр по организации"
            className="rounded-full p-1 text-gray-500 hover:bg-gray-200"
            onClick={() => onChange({ companyId: null })}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
      )}

      {anySet && (
        <Button type="button" variant="outline" size="sm" className={height} data-testid="location-filters-reset" onClick={onReset}>
          Сбросить
        </Button>
      )}
    </>
  );
}
